import type { Table } from 'dexie'
import type { BaseOutbox, ConfigCoffre } from '../../outbox/base'
import {
  envelopperParCode,
  envelopperParPasskey,
  ErreurCode,
  nouvelleCle,
  ouvrirParCode,
  ouvrirParPasskey,
  type EnveloppeCode,
} from './cles'

// Verrouillage de l'app (chiffrement local) : activation, déverrouillage par
// code ou passkey, verrouillage après inactivité et au retour d'arrière-plan.
//
// Limite : le chiffrement protège un appareil volé verrouillé ou les fichiers du
// profil navigateur, pas un appareil déverrouillé et compromis. Un code de 6
// chiffres se casse hors ligne en quelques heures sur du matériel dédié : plus
// le code est long, mieux c'est.

export type EtatVerrou = 'chargement' | 'inactif' | 'verrouille' | 'ouvert'

export const VERROUILLAGE_MINUTES = 5
// Retour d'arrière-plan : délai de grâce pour un partage ou un appel bref
const GRACE_ARRIERE_PLAN_MS = 60_000
// Tentatives de code : au-delà, attente croissante (ralentit un essai à la main)
const ESSAIS_LIBRES = 5

const tables = (base: BaseOutbox) =>
  [base.clients, base.articles, base.devis, base.situations, base.chantiers, base.pointages, base.journal, base.outbox, base.meta] as unknown as Table<unknown, unknown>[]

export function creerVerrou(base: BaseOutbox) {
  let etat: EtatVerrou = 'chargement'
  let config: ConfigCoffre | undefined
  let echecs = 0
  let bloqueJusqua = 0
  const abonnes = new Set<() => void>()
  const canal = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(`filaplomb-verrou-${base.name}`) : null

  function changer(e: EtatVerrou) {
    etat = e
    abonnes.forEach((f) => f())
  }

  function ouvrir(cle: Uint8Array) {
    base.chiffrement.ouvrir(cle)
    echecs = 0
    changer('ouvert')
  }

  /** Réécrit toutes les tables dans une seule transaction (en clair ou chiffrées selon l'état du coffre). */
  async function reecrire(avant: () => void, apresConfig: () => Promise<unknown>) {
    await base.transaction('rw', [...tables(base), base.coffre], async () => {
      const contenus = await Promise.all(tables(base).map((t) => t.toArray()))
      avant()
      await Promise.all(tables(base).map((t, i) => t.bulkPut(contenus[i])))
      await apresConfig()
    })
  }

  canal?.addEventListener('message', ({ data }) => {
    if (data === 'verrouiller' && etat === 'ouvert') verrou.verrouiller(false)
    // Activation ou désactivation dans un autre onglet : l'état du coffre a changé sous nos pieds
    if (data === 'configuration') location.reload()
  })

  const verrou = {
    etat: () => etat,
    abonner(f: () => void) {
      abonnes.add(f)
      return () => abonnes.delete(f)
    },
    config: () => config,

    async initialiser() {
      config = await base.coffre.get('config')
      if (config) base.chiffrement.actif = true
      changer(config ? 'verrouille' : 'inactif')
    },

    /** Secondes d'attente avant le prochain essai (0 : essai possible). */
    attente: () => Math.max(0, Math.ceil((bloqueJusqua - Date.now()) / 1000)),

    async deverrouillerParCode(code: string) {
      if (!config) return
      if (verrou.attente() > 0) throw new Error(`Patientez ${verrou.attente()} s avant de réessayer.`)
      try {
        ouvrir(await ouvrirParCode(config.code, code))
      } catch (e) {
        if (e instanceof ErreurCode && ++echecs >= ESSAIS_LIBRES) bloqueJusqua = Date.now() + 2 ** (echecs - ESSAIS_LIBRES) * 30_000
        throw e
      }
    },

    async deverrouillerParPasskey() {
      if (!config?.passkey) return
      ouvrir(await ouvrirParPasskey(config.passkey))
    },

    verrouiller(diffuser = true) {
      if (etat !== 'ouvert') return
      base.chiffrement.verrouiller()
      changer('verrouille')
      if (diffuser) canal?.postMessage('verrouiller')
    },

    /** Chiffre toutes les données existantes avec une nouvelle clé, protégée par le code. */
    async activer(code: string, iterations?: number) {
      if (etat !== 'inactif') return
      const cle = nouvelleCle()
      const nouvelle: ConfigCoffre = {
        cle: 'config',
        code: await envelopperParCode(cle, code, iterations),
        verrouillageMinutes: VERROUILLAGE_MINUTES,
      }
      try {
        await reecrire(
          () => base.chiffrement.ouvrir(cle),
          () => base.coffre.put(nouvelle),
        )
      } catch (e) {
        base.chiffrement.desactiver()
        throw e
      }
      config = nouvelle
      changer('ouvert')
      canal?.postMessage('configuration')
    },

    /** Remet toutes les données en clair (app ouverte uniquement). */
    async desactiver() {
      if (etat !== 'ouvert') return
      try {
        await reecrire(
          () => (base.chiffrement.actif = false),
          () => base.coffre.delete('config'),
        )
      } catch (e) {
        base.chiffrement.actif = true
        throw e
      }
      base.chiffrement.desactiver()
      config = undefined
      changer('inactif')
      canal?.postMessage('configuration')
    },

    async changerCode(code: string, iterations?: number) {
      if (etat !== 'ouvert' || !config) return
      const enveloppe: EnveloppeCode = await envelopperParCode(base.chiffrement.cleCourante(), code, iterations)
      config = { ...config, code: enveloppe }
      await base.coffre.put(config)
    },

    async ajouterPasskey() {
      if (etat !== 'ouvert' || !config) return
      config = { ...config, passkey: await envelopperParPasskey(base.chiffrement.cleCourante()) }
      await base.coffre.put(config)
    },

    async retirerPasskey() {
      if (!config?.passkey) return
      const { passkey: _, ...reste } = config
      config = reste
      await base.coffre.put(config)
    },

    /**
     * Verrouille après inactivité et au retour d'arrière-plan (au-delà d'un
     * délai de grâce). Renvoie la fonction d'arrêt.
     */
    surveiller(): () => void {
      let minuterie: ReturnType<typeof setTimeout> | undefined
      let cacheLe = 0
      const relancer = () => {
        clearTimeout(minuterie)
        if (etat === 'ouvert' && config) {
          minuterie = setTimeout(() => verrou.verrouiller(false), config.verrouillageMinutes * 60_000)
        }
      }
      const visibilite = () => {
        if (document.visibilityState === 'hidden') cacheLe = Date.now()
        else if (cacheLe && Date.now() - cacheLe > GRACE_ARRIERE_PLAN_MS) verrou.verrouiller(false)
        else relancer()
      }
      const evenements = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const
      evenements.forEach((e) => window.addEventListener(e, relancer, { passive: true }))
      document.addEventListener('visibilitychange', visibilite)
      const desabonner = verrou.abonner(relancer)
      relancer()
      return () => {
        clearTimeout(minuterie)
        desabonner()
        evenements.forEach((e) => window.removeEventListener(e, relancer))
        document.removeEventListener('visibilitychange', visibilite)
      }
    },
  }
  return verrou
}

export type Verrou = ReturnType<typeof creerVerrou>
